import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link,
} from '@mui/material';
import { brandColors } from '../theme';

export const PrivacyPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: { xs: 3, md: 6 } }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 600,
            color: brandColors.indigo,
            mb: 1,
          }}
        >
          Privacy Policy
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ '& > *': { mb: 3 } }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              1. Introduction
            </Typography>
            <Typography variant="body1" paragraph>
              Barrett HTL Inc ("we," "our," or "us") is committed to protecting your privacy and ensuring 
              the security of your personal information. This Privacy Policy explains how we collect, 
              use, disclose, and safeguard your information when you use our Licensee Portal and 
              related services (collectively, the "Services").
            </Typography>
            <Typography variant="body1" paragraph>
              This policy complies with the General Data Protection Regulation (GDPR) and other 
              applicable data protection laws. By using our Services, you consent to the data 
              practices described in this policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              2. Information We Collect
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              2.1 Personal Information
            </Typography>
            <Typography variant="body1" paragraph>
              We collect information that you provide directly to us, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Name and contact information (email address, phone number)</Typography>
              <Typography component="li" variant="body1">Company name and job title</Typography>
              <Typography component="li" variant="body1">Account credentials and authentication data</Typography>
              <Typography component="li" variant="body1">Payment information (processed securely through third-party payment processors)</Typography>
              <Typography component="li" variant="body1">Communications and correspondence with us</Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 500, mt: 2, mb: 1 }}>
              2.2 Usage Information
            </Typography>
            <Typography variant="body1" paragraph>
              We automatically collect certain information when you use our Services:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Log data (IP address, browser type, operating system)</Typography>
              <Typography component="li" variant="body1">Device information and identifiers</Typography>
              <Typography component="li" variant="body1">Usage patterns and preferences</Typography>
              <Typography component="li" variant="body1">Performance data and analytics</Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              3. Legal Basis for Processing (GDPR)
            </Typography>
            <Typography variant="body1" paragraph>
              Under the GDPR, we process your personal data based on the following legal grounds:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">
                <strong>Contract Performance:</strong> Processing necessary to fulfill our contractual obligations to you
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Legitimate Interests:</strong> Processing for our legitimate business interests, such as improving our services
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Legal Compliance:</strong> Processing necessary to comply with legal obligations
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Consent:</strong> Where you have provided explicit consent for specific processing activities
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              4. How We Use Your Information
            </Typography>
            <Typography variant="body1" paragraph>
              We use the collected information for the following purposes:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Provide, maintain, and improve our Services</Typography>
              <Typography component="li" variant="body1">Process transactions and manage subscriptions</Typography>
              <Typography component="li" variant="body1">Send administrative information and service updates</Typography>
              <Typography component="li" variant="body1">Respond to inquiries and provide customer support</Typography>
              <Typography component="li" variant="body1">Monitor and analyze usage patterns to improve user experience</Typography>
              <Typography component="li" variant="body1">Detect, prevent, and address technical issues or fraud</Typography>
              <Typography component="li" variant="body1">Comply with legal obligations and enforce our terms</Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              5. Data Sharing and Disclosure
            </Typography>
            <Typography variant="body1" paragraph>
              We do not sell, trade, or rent your personal information. We may share your information in the following circumstances:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">
                <strong>Service Providers:</strong> With trusted third-party vendors who assist in operating our Services
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Legal Requirements:</strong> When required by law, court order, or governmental request
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Consent:</strong> With your explicit consent for specific purposes
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              6. Your Rights Under GDPR
            </Typography>
            <Typography variant="body1" paragraph>
              If you are located in the European Economic Area (EEA), you have the following rights:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">
                <strong>Access:</strong> Request a copy of your personal data
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Rectification:</strong> Request correction of inaccurate or incomplete data
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Restriction:</strong> Request restriction of processing your personal data
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Portability:</strong> Request transfer of your data to another service provider
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Objection:</strong> Object to processing based on legitimate interests
              </Typography>
              <Typography component="li" variant="body1">
                <strong>Withdraw Consent:</strong> Withdraw consent for processing where applicable
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              To exercise these rights, please contact us at legal@barretthtl.com. We will respond to your request within 30 days.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              7. Data Security
            </Typography>
            <Typography variant="body1" paragraph>
              We implement appropriate technical and organizational measures to protect your personal data against:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Unauthorized access or disclosure</Typography>
              <Typography component="li" variant="body1">Accidental or unlawful destruction</Typography>
              <Typography component="li" variant="body1">Loss, alteration, or misuse</Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              These measures include encryption, access controls, regular security assessments, and employee training. 
              However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              8. Data Retention
            </Typography>
            <Typography variant="body1" paragraph>
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this 
              Privacy Policy, unless a longer retention period is required or permitted by law. The criteria used to 
              determine retention periods include:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">The duration of our ongoing relationship with you</Typography>
              <Typography component="li" variant="body1">Legal obligations requiring data retention</Typography>
              <Typography component="li" variant="body1">Advisability in light of our legal position</Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              9. International Data Transfers
            </Typography>
            <Typography variant="body1" paragraph>
              Your information may be transferred to and processed in countries other than your country of residence. 
              These countries may have data protection laws different from those in your country. We ensure appropriate 
              safeguards are in place for such transfers, including:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Standard Contractual Clauses approved by the European Commission</Typography>
              <Typography component="li" variant="body1">Adequacy decisions by relevant data protection authorities</Typography>
              <Typography component="li" variant="body1">Other legally recognized transfer mechanisms</Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              10. Cookies and Tracking Technologies
            </Typography>
            <Typography variant="body1" paragraph>
              We use cookies and similar tracking technologies to:
            </Typography>
            <Box component="ul" sx={{ pl: 3 }}>
              <Typography component="li" variant="body1">Maintain your session and authentication status</Typography>
              <Typography component="li" variant="body1">Remember your preferences and settings</Typography>
              <Typography component="li" variant="body1">Analyze usage patterns and improve our Services</Typography>
              <Typography component="li" variant="body1">Provide personalized content and features</Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              You can manage cookie preferences through your browser settings. Note that disabling certain cookies 
              may limit the functionality of our Services.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              11. Children's Privacy
            </Typography>
            <Typography variant="body1" paragraph>
              Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal 
              information from children. If we become aware that we have collected personal information from a child 
              without parental consent, we will take steps to delete that information.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              12. Third-Party Services
            </Typography>
            <Typography variant="body1" paragraph>
              Our Services may contain links to third-party websites or services. We are not responsible for the 
              privacy practices of these third parties. We encourage you to review their privacy policies before 
              providing any personal information.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              13. Updates to This Policy
            </Typography>
            <Typography variant="body1" paragraph>
              We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. 
              We will notify you of any material changes by posting the updated policy on our Services and updating the 
              "Last updated" date. Your continued use of our Services after such changes constitutes acceptance of the 
              updated policy.
            </Typography>
          </Box>

          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: brandColors.indigo }}>
              14. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
              please contact us at:
            </Typography>
            <Box sx={{ pl: 3 }}>
              <Typography variant="body1">
                <strong>Barrett HTL Inc</strong>
              </Typography>
              <Typography variant="body1">
                Email: <Link href="mailto:legal@barretthtl.com">legal@barretthtl.com</Link>
              </Typography>
              <Typography variant="body1">
                Data Protection Officer: <Link href="mailto:dpo@barretthtl.com">dpo@barretthtl.com</Link>
              </Typography>
            </Box>
            <Typography variant="body1" paragraph sx={{ mt: 2 }}>
              You also have the right to lodge a complaint with your local data protection supervisory authority 
              if you believe our processing of your personal data violates applicable law.
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};